import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './PriceForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import DuplicateBanner from '../../../shared/DuplicateBanner';

function productLabel(p) {
  return `${p.sku} — ${p.name}`;
}

function customerLabel(c) {
  return c.name;
}

function initialProductScopeType(initial) {
  if (initial?.product_price_group_id) return 'group';
  if (initial?.product_id) return 'specific';
  return 'all';
}

function initialCustomerScopeType(initial) {
  if (initial?.customer_id) return 'specific';
  if (initial?.customer_price_group_id) return 'group';
  return 'all';
}

// Add-only: sets Product/Customer Scope and UoM, the one moment a Price's
// Scope is ever chosen — Scope is fixed for the life of the record after
// this (see PricePricingPage.js). Always creates one open-ended period
// (start_date/end_date both null); Effective Dates for a specific period
// are set afterward on the Pricing page's own Schedule table, never here.
// How this Price is priced (Rule vs Fixed Override, and everything that
// forks from that choice — a Rule reference, or a Breaks schedule) lives
// entirely on its own Pricing page, never here either — Add creates a bare
// record (defaults to Fixed Override, "Needs price") and goes straight
// there. `initial` only ever pre-fills a Duplicate (a new record seeded
// from an existing one's Scope) — there is no Edit path through this form.
function PriceForm({ initial, onSave, onCancel, loading }) {
  const [productScopeType, setProductScopeType] = useState(initialProductScopeType(initial));
  const [productPriceGroupId, setProductPriceGroupId] = useState(initial?.product_price_group_id ? String(initial.product_price_group_id) : '');
  const [productId, setProductId] = useState(initial?.product_id ? String(initial.product_id) : '');
  const [productSearch, setProductSearch] = useState(initial?.product ? productLabel(initial.product) : '');
  const [productResults, setProductResults] = useState([]);
  const [showProductResults, setShowProductResults] = useState(false);
  const skipNextProductSearch = useRef(true);

  const [customerScopeType, setCustomerScopeType] = useState(initialCustomerScopeType(initial));
  const [customerPriceGroupId, setCustomerPriceGroupId] = useState(initial?.customer_price_group_id ? String(initial.customer_price_group_id) : '');
  const [customerId, setCustomerId] = useState(initial?.customer_id ? String(initial.customer_id) : '');
  const [customerSearch, setCustomerSearch] = useState(initial?.customer ? customerLabel(initial.customer) : '');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const skipNextCustomerSearch = useRef(true);

  const [uomId, setUomId] = useState(initial?.uom_id ? String(initial.uom_id) : '');
  const [error, setError] = useState('');

  const [productGroups, setProductGroups] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [uoms, setUoms] = useState([]);
  // A specific Product's own UoM rows, rather than the tenant-wide list
  // above — a UoM this Product was never set up with isn't offered. Narrowed
  // below to its sellable ones (can_sell = true), except for Retail, whose
  // Price is only ever its Base Price, entered on the Base UoM whether or
  // not that's sellable (retailPriceService.js). null (not just empty)
  // means "not applicable" — Product Price Group/All Products scope has no
  // single Product to narrow against, so the field falls back to the
  // generic `uoms` list above in that case.
  const [productUomRows, setProductUomRows] = useState(null);

  useEffect(() => {
    axios.get('/api/product-price-groups').then((res) => setProductGroups((res.data.data || []).filter((g) => g.is_active))).catch(() => setProductGroups([]));
    axios.get('/api/customer-price-groups').then((res) => setCustomerGroups((res.data.data || []).filter((g) => g.is_active))).catch(() => setCustomerGroups([]));
    axios.get('/api/uoms').then((res) => setUoms((res.data.data || []).filter((u) => u.is_active))).catch(() => setUoms([]));
  }, []);

  useEffect(() => {
    if (productScopeType !== 'specific' || !productId) {
      setProductUomRows(null);
      return;
    }
    axios.get(`/api/products/${productId}/uoms`)
      .then((res) => setProductUomRows(res.data.data?.uoms || []))
      .catch(() => setProductUomRows([]));
  }, [productScopeType, productId]);

  const isRetailScope = customerScopeType === 'group'
    && !!customerGroups.find((g) => String(g.id) === String(customerPriceGroupId))?.is_system_default;
  const uomOptions = productScopeType === 'specific' && productId && productUomRows !== null
    ? productUomRows
      .filter((pu) => (isRetailScope ? pu.is_base : pu.can_sell))
      .map((pu) => ({ id: pu.uom_id, name: pu.name }))
    : uoms;

  // Same live search-as-you-type pattern as the Product list's SKU/name search.
  useEffect(() => {
    if (skipNextProductSearch.current) {
      skipNextProductSearch.current = false;
      return;
    }
    if (!productSearch.trim()) {
      setProductResults([]);
      setShowProductResults(false);
      return;
    }
    const timer = setTimeout(() => {
      axios.get('/api/products', { params: { search: productSearch, status: 'active', limit: 10 } })
        .then((res) => {
          setProductResults(res.data.data || []);
          setShowProductResults(true);
        })
        .catch(() => setProductResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [productSearch]);

  // Whatever UoM was picked for the old Product may not be sellable (or
  // may not even exist) for the new one — same reasoning as the sellable-
  // only dropdown itself, so it's cleared rather than left stale and
  // silently wrong.
  const handleSelectProduct = (p) => {
    skipNextProductSearch.current = true;
    setProductId(String(p.id));
    setProductSearch(productLabel(p));
    setShowProductResults(false);
    setUomId('');
  };

  const handleClearProduct = () => {
    skipNextProductSearch.current = true;
    setProductId('');
    setProductSearch('');
    setShowProductResults(false);
    setUomId('');
  };

  // Same live search-as-you-type pattern as the Product picker.
  useEffect(() => {
    if (skipNextCustomerSearch.current) {
      skipNextCustomerSearch.current = false;
      return;
    }
    if (!customerSearch.trim()) {
      setCustomerResults([]);
      setShowCustomerResults(false);
      return;
    }
    const timer = setTimeout(() => {
      axios.get('/api/customers', { params: { scope: 'quotable', search: customerSearch, status: 'active', limit: 10 } })
        .then((res) => {
          setCustomerResults(res.data.data || []);
          setShowCustomerResults(true);
        })
        .catch(() => setCustomerResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const handleSelectCustomer = (c) => {
    skipNextCustomerSearch.current = true;
    setCustomerId(String(c.id));
    setCustomerSearch(customerLabel(c));
    setShowCustomerResults(false);
  };

  const handleClearCustomer = () => {
    skipNextCustomerSearch.current = true;
    setCustomerId('');
    setCustomerSearch('');
    setShowCustomerResults(false);
  };

  const validate = () => {
    if (productScopeType === 'group' && !productPriceGroupId) return 'Select a Product Price Group.';
    if (productScopeType === 'specific' && productSearch.trim() && !productId) return 'Select a product from the list, or clear the search to apply to all products.';
    if (productScopeType === 'specific' && !productId) return 'Search for and select a Product.';
    if (customerScopeType === 'group' && !customerPriceGroupId) return 'Select a Customer Price Group.';
    if (customerScopeType === 'specific' && customerSearch.trim() && !customerId) return 'Select a customer from the list, or clear the search to apply to all customers.';
    if (customerScopeType === 'specific' && !customerId) return 'Search for and select a Customer.';
    if (!uomId) return 'Select a Unit of Measure.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');

    const payload = {
      product_id: productScopeType === 'specific' ? Number(productId) : null,
      product_price_group_id: productScopeType === 'group' ? Number(productPriceGroupId) : null,
      customer_id: customerScopeType === 'specific' ? Number(customerId) : null,
      customer_price_group_id: customerScopeType === 'group' ? Number(customerPriceGroupId) : null,
      uom_id: uomId ? Number(uomId) : null,
      start_date: null,
      end_date: null,
    };

    onSave(payload);
  };

  return (
    <form className="price-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      {initial?._duplicateSourceId && <DuplicateBanner label="Price" />}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label>Customer Scope<RequiredMark /></label><br />
          <select className="styled-input" value={customerScopeType} onChange={(e) => setCustomerScopeType(e.target.value)}>
            <option value="all">All</option>
            <option value="group">Customer Price Group</option>
            <option value="specific">Specific Customer</option>
          </select>
        </div>
        {customerScopeType === 'group' && (
          <div>
            <label>Customer Price Group<RequiredMark /></label><br />
            <select className="styled-input" value={customerPriceGroupId} onChange={(e) => setCustomerPriceGroupId(e.target.value)} style={{ width: 220 }}>
              <option value="">Select a group…</option>
              {customerGroups.map((g) => (
                <option key={g.id} value={g.id}>{'   '.repeat(g.depth || 0)}{g.name}</option>
              ))}
            </select>
            <Link
              to="/sales-customer-price-groups"
              style={{ marginLeft: 10, color: '#1976d2', fontSize: 13, textDecoration: 'underline' }}
            >
              Manage groups
            </Link>
          </div>
        )}
        {customerScopeType === 'specific' && (
          <div style={{ position: 'relative' }}>
            <label>Customer<RequiredMark /></label><br />
            <input
              className="styled-input"
              placeholder="Search code or name..."
              value={customerSearch}
              onChange={(e) => { setCustomerId(''); setCustomerSearch(e.target.value); }}
              onFocus={() => { if (customerResults.length) setShowCustomerResults(true); }}
              onBlur={() => setTimeout(() => setShowCustomerResults(false), 150)}
              style={{ width: 300 }}
            />
            {customerId && (
              <button type="button" onClick={handleClearCustomer} title="Clear customer" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
            )}
            {showCustomerResults && customerResults.length > 0 && (
              <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, width: 300, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                {customerResults.map((c) => (
                  <li
                    key={c.id}
                    onMouseDown={() => handleSelectCustomer(c)}
                    style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {customerLabel(c)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 14, alignItems: 'flex-end' }}>
        <div>
          <label>Product Scope<RequiredMark /></label><br />
          <select className="styled-input" value={productScopeType} onChange={(e) => setProductScopeType(e.target.value)}>
            <option value="all">All</option>
            <option value="group">Product Group</option>
            <option value="specific">Specific Product</option>
          </select>
        </div>
        {productScopeType === 'group' && (
          <div>
            <label>Product Price Group<RequiredMark /></label><br />
            <select className="styled-input" value={productPriceGroupId} onChange={(e) => setProductPriceGroupId(e.target.value)} style={{ width: 220 }}>
              <option value="">Select a group…</option>
              {productGroups.map((g) => (
                <option key={g.id} value={g.id}>{'   '.repeat(g.depth || 0)}{g.name}</option>
              ))}
            </select>
            <Link
              to="/sales-product-price-groups"
              style={{ marginLeft: 10, color: '#1976d2', fontSize: 13, textDecoration: 'underline' }}
            >
              Manage groups
            </Link>
          </div>
        )}
        {productScopeType === 'specific' && (
          <div style={{ position: 'relative' }}>
            <label>Product<RequiredMark /></label><br />
            <input
              className="styled-input"
              placeholder="Search name or SKU..."
              value={productSearch}
              onChange={(e) => { setProductId(''); setProductSearch(e.target.value); }}
              onFocus={() => { if (productResults.length) setShowProductResults(true); }}
              onBlur={() => setTimeout(() => setShowProductResults(false), 150)}
              style={{ width: 440 }}
            />
            {productId && (
              <button type="button" onClick={handleClearProduct} title="Clear product" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
            )}
            {showProductResults && productResults.length > 0 && (
              <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, width: 440, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                {productResults.map((p) => (
                  <li
                    key={p.id}
                    onMouseDown={() => handleSelectProduct(p)}
                    style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {productLabel(p)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div>
          <label>Unit of Measure<RequiredMark /></label><br />
          <select className="styled-input" value={uomId} onChange={(e) => setUomId(e.target.value)}>
            <option value="">Select a UoM…</option>
            {uomOptions.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default PriceForm;
